<script setup lang="ts">
import { UserSetting } from "@/helpers/settings";
import { settingName, defaultSettings as settings } from "@/helpers/constants";
import { useSettingsStore } from "@/stores/settings";
import { useUiVisibilityStore } from "@/stores/uiVisibility";
import AccessibleButton from "./AccessibleButton.vue";
import { watch } from "vue";
import { useMediaQuery } from "@/helpers/useMediaQuery";

const settingsStore = useSettingsStore();
const visibilityStore = useUiVisibilityStore();

defineProps<{
  isPannable: boolean;
}>();

const hasMouse = useMediaQuery(
  "(any-hover: hover) and (any-pointer: fine)",
  true
);

// Disable hand tool if no mouse is detected to avoid users getting stuck in hand tool mode on touch devices (e.g. tablets or desktop touchscreen monitors)
watch(
  hasMouse,
  (mouseAvailable) => {
    if (!mouseAvailable) {
      settingsStore.perContent.handTool = false;
    }
  },
  { immediate: true }
);

const handTool = settings.find(
  (setting: UserSetting) => setting.name === settingName.handTool
);

function toggleHandTool(event: MouseEvent) {
  settingsStore.perContent.handTool = !settingsStore.perContent.handTool;
  (event.currentTarget as HTMLElement)?.blur();
}
</script>

<template>
  <div
    v-if="isPannable && hasMouse"
    :class="[
      'cursor-tool',
      {
        'cursor-tool--hidden': !visibilityStore.showUI,
      },
    ]"
  >
    <AccessibleButton
      class="cursor-tool__button"
      :title="
        settingsStore.perContent.handTool
          ? $t('settings.cursorOptions.select')
          : $t('settings.cursorOptions.hand')
      "
      :aria-label="
        settingsStore.perContent.handTool
          ? $t('settings.cursorOptions.select')
          : $t('settings.cursorOptions.hand')
      "
      show-on-hover
      @click="toggleHandTool"
    >
      <template #icon>
        <span class="material-icons" aria-hidden="true">{{
          settingsStore.perContent.handTool
            ? handTool.options[0].icon
            : handTool.options[1].icon
        }}</span>
      </template>
    </AccessibleButton>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/styles/reader.scss" as *;

.cursor-tool {
  opacity: 1;
  width: $squareButtonSize;
  height: $squareButtonSize;
  min-width: $squareButtonSize;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  transition:
    height 0s,
    width 0s,
    right 0.8s cubic-bezier(0, 1, 0.5, 1),
    opacity 0.8s cubic-bezier(0, 1, 0.5, 1);

  &--hidden {
    opacity: 0;
  }

  &__button {
    border-radius: $spacingS;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--DM__theme-font-color);
    background-color: var(--DM__theme-fill-color);
    @include hover();
  }
}
</style>
